import React, { useState } from 'react';
import { Search, BookOpen, Code, Battery, ChevronDown, Menu, X, Users, BookMarked, Award, TrendingUp, ArrowRight, Download, Play, ExternalLink, Github, Star, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';

const features = [
  {
    icon: Code,
    title: "Project-Based Learning",
    description: "Build real applications and solve practical programming challenges with hands-on projects designed for the tech industry.",
    highlight: "Real Projects"
  },
  {
    icon: Users,
    title: "Expert Mentorship", 
    description: "Learn from experienced software engineers with personalized guidance and code reviews for rapid skill development.",
    highlight: "1-on-1 Support"
  },
  {
    icon: Battery,
    title: "Industry Standards",
    description: "Master modern development practices, version control, and professional coding standards used in top tech companies.",
    highlight: "Production Ready"
  }
];



const MainHomepage = () => {
  const { language } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCoursesDropdownOpen, setIsCoursesDropdownOpen] = useState(false);


  const handleCourseClick = (link) => {
    window.location.href = link;
  };
  
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/qubit-network-logo.svg" 
                alt="Qubit Center" 
                className="w-8 h-8"
                style={{ filter: 'brightness(0) saturate(100%)' }}
              />
              <span className="text-xl font-semibold text-gray-900 dark:text-white">
                Qubit Center
              </span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {language === 'ar' ? 'المميزات' : 'Features'}
              </a>
              
              <div className="relative">
                <button 
                  onClick={() => setIsCoursesDropdownOpen(!isCoursesDropdownOpen)}
                  className="flex items-center gap-1 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  {language === 'ar' ? 'الدورات' : 'Courses'}
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {isCoursesDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="p-2 space-y-1">
                      <button 
                        onClick={() => handleCourseClick("/cpp-course")}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <Code className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {language === 'ar' ? 'أساسيات البرمجة بـ C++' : 'C++ Programming Basics'}
                              </div>
                              <div className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400 px-2 py-0.5 rounded text-xs font-bold">
                                {language === 'ar' ? 'متاح' : 'OPEN'}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {language === 'ar' ? 'تعلم البرمجة من الصفر - حضورياً في عمان' : 'Programming from zero - In-person in Amman'}
                            </div>
                          </div>
                        </div>
                      </button>
                      
                      <button 
                        onClick={() => handleCourseClick("/lithium-teaser")}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <Battery className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {language === 'ar' ? 'أنظمة بطاريات الليثيوم' : 'Lithium Battery Systems'}
                              </div>
                              <div className="bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded text-xs font-bold">
                                {language === 'ar' ? 'قريباً' : 'SOON'}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {language === 'ar' ? 'بناء وتصميم أنظمة البطاريات المتقدمة' : 'Advanced battery systems design & build'}
                            </div>
                          </div>
                        </div>
                      </button>

                      <button 
                        onClick={() => handleCourseClick("/solar-teaser")}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex items-center justify-center">☀️</div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {language === 'ar' ? 'أنظمة الطاقة الشمسية' : 'Solar Energy Systems'}
                              </div>
                              <div className="bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded text-xs font-bold">
                                {language === 'ar' ? 'قريباً' : 'SOON'}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {language === 'ar' ? 'تصميم وتركيب أنظمة الطاقة الشمسية' : 'Solar system design & installation'}
                            </div>
                          </div>
                        </div>
                      </button>

                      <button 
                        onClick={() => handleCourseClick("/drone-teaser")}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex items-center justify-center">🛸</div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {language === 'ar' ? 'بناء طائرة بدون طيار' : 'Build Drone with PX4'}
                              </div>
                              <div className="bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded text-xs font-bold">
                                {language === 'ar' ? 'قريباً' : 'SOON'}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              {language === 'ar' ? 'بناء وبرمجة طائرة بدون طيار مع PX4' : 'Build & program drone with PX4 autopilot'}
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <a href="#about" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {language === 'ar' ? 'من نحن' : 'About'}
              </a>
              
              <a href="#contact" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {language === 'ar' ? 'التواصل' : 'Contact'}
              </a>
            </nav>
            
            <div className="flex items-center gap-4">
              <LanguageToggle variant="button" />
              
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden">
          {isMenuOpen && (
            <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-6">
              <div className="space-y-4">
                <a href="#features" className="block text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                  {language === 'ar' ? 'المميزات' : 'Features'}
                </a>
                
                <div className="space-y-2">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {language === 'ar' ? 'الدورات' : 'Courses'}
                  </div>
                  <div className="pl-4 space-y-2">
                    <button onClick={() => handleCourseClick("/cpp-course")} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                      {language === 'ar' ? 'أساسيات البرمجة بـ C++' : 'C++ Programming Basics'}
                      <div className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400 px-2 py-0.5 rounded text-xs font-bold">
                        {language === 'ar' ? 'متاح' : 'OPEN'}
                      </div>
                    </button>
                    <button onClick={() => handleCourseClick("/lithium-teaser")} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                      {language === 'ar' ? 'أنظمة بطاريات الليثيوم' : 'Lithium Battery Systems'}
                      <div className="bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded text-xs font-bold">
                        {language === 'ar' ? 'قريباً' : 'SOON'}
                      </div>
                    </button>
                    <button onClick={() => handleCourseClick("/solar-teaser")} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                      {language === 'ar' ? 'أنظمة الطاقة الشمسية' : 'Solar Energy Systems'}
                      <div className="bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded text-xs font-bold">
                        {language === 'ar' ? 'قريباً' : 'SOON'}
                      </div>
                    </button>
                    <button onClick={() => handleCourseClick("/drone-teaser")} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                      {language === 'ar' ? 'بناء طائرة بدون طيار' : 'Build Drone with PX4'}
                      <div className="bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded text-xs font-bold">
                        {language === 'ar' ? 'قريباً' : 'SOON'}
                      </div>
                    </button>
                  </div>
                </div>
                
                <a href="#about" className="block text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                  {language === 'ar' ? 'من نحن' : 'About'}
                </a>
                
                <a href="#contact" className="block text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                  {language === 'ar' ? 'التواصل' : 'Contact'}
                </a>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-full text-sm font-medium mb-8">
              <Star className="w-4 h-4" />
              {language === 'ar' ? 'منصة تعليمية متطورة' : 'Advanced Learning Platform'}
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              {language === 'ar' ? (
                <>تعلم التقنيات<br />المتقدمة معنا</>
              ) : (
                <>Learn Advanced<br />Technology with Us</>
              )}
            </h1>
            
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
              {language === 'ar' 
                ? 'اكتسب مهارات عملية في البطاريات والبرمجة من خلال دوراتنا التفاعلية المصممة للمبتدئين والمحترفين.'
                : 'Gain practical skills in battery technology and programming through our interactive courses designed for beginners and professionals.'
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => handleCourseClick("/cpp-course")}
                className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors flex items-center gap-2"
              >
                {language === 'ar' ? 'ابدأ التعلم' : 'Start Learning'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800"></div>
        <div className="absolute inset-0 -z-10 bg-grid-pattern opacity-5"></div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {language === 'ar' ? 'لماذا تختارنا؟' : 'Why Choose Us?'}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {language === 'ar' 
                ? 'تجربة تعليمية شاملة تجمع بين النظرية والتطبيق العملي'
                : 'A comprehensive learning experience combining theory with hands-on practice'
              }
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors group">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    {language === 'ar' ? (
                      feature.title === 'Project-Based Learning' ? 'التعلم القائم على المشاريع' :
                      feature.title === 'Expert Mentorship' ? 'الإرشاد الخبير' : 'معايير الصناعة'
                    ) : feature.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                    {language === 'ar' ? (
                      feature.description === 'Build real applications and solve practical programming challenges with hands-on projects designed for the tech industry.' 
                        ? 'ابن تطبيقات حقيقية وحل تحديات البرمجة العملية مع مشاريع عملية مصممة لصناعة التقنية.' 
                        : feature.description === 'Learn from experienced software engineers with personalized guidance and code reviews for rapid skill development.'
                        ? 'تعلم من مهندسي البرمجيات ذوي الخبرة مع توجيه شخصي ومراجعات الكود لتطوير مهارات سريع.'
                        : 'أتقن ممارسات التطوير الحديثة وأنظمة التحكم في النسخ ومعايير البرمجة المهنية المستخدمة في أفضل الشركات التقنية.'
                    ) : feature.description}
                  </p>
                  
                  <div className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold">
                    <Check className="w-4 h-4" />
                    {feature.highlight}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-800/50 dark:via-gray-800/30 dark:to-gray-900/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {language === 'ar' ? 'برامجنا التقنية' : 'Our Tech Programs'}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {language === 'ar' ? 'اختر المسار التقني المناسب لمستقبلك' : 'Choose the tech path that shapes your future'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {/* C++ Programming Course - AVAILABLE */}
            <div 
              onClick={() => handleCourseClick("/cpp-course")}
              className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300 cursor-pointer group hover:shadow-2xl hover:scale-105"
            >
              <div className="h-48 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    {language === 'ar' ? 'متاح الآن' : 'AVAILABLE NOW'}
                  </div>
                </div>
                <div className="absolute top-4 right-4">
                  <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">
                    {language === 'ar' ? '⭐ الأكثر طلباً' : '⭐ FEATURED'}
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 text-white">
                  <Code className="w-8 h-8" />
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {language === 'ar' ? 'أساسيات البرمجة بـ C++' : 'C++ Programming Basics'}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {language === 'ar' 
                    ? 'ابدأ رحلتك في البرمجة من الصفر مع دورة C++ العملية. تعلم بناء التطبيقات الحقيقية وحل المشاكل البرمجية.'
                    : 'Start your programming journey from zero with hands-on C++ course. Learn to build real applications and solve coding challenges.'
                  }
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span>12 {language === 'ar' ? 'أسابيع' : 'weeks'}</span>
                    <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-400 px-2 py-1 rounded text-xs">
                      {language === 'ar' ? 'حضورياً' : 'In-Person'}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </div>

            {/* Lithium Batteries Course - COMING SOON */}
            <div 
              onClick={() => handleCourseClick("/lithium-teaser")}
              className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300 cursor-pointer group hover:shadow-2xl hover:scale-105"
            >
              <div className="h-48 bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-600 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <div className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    {language === 'ar' ? 'قريباً' : 'COMING SOON'}
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 text-white">
                  <Battery className="w-8 h-8" />
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {language === 'ar' ? 'أنظمة بطاريات الليثيوم' : 'Lithium Battery Systems'}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {language === 'ar' 
                    ? 'تعلم بناء وتصميم أنظمة البطاريات المتقدمة مع تقنيات إعادة التدوير والطاقة المستدامة.'
                    : 'Learn to build and design advanced battery systems with recycling techniques and sustainable energy solutions.'
                  }
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span>6 {language === 'ar' ? 'أسابيع' : 'weeks'}</span>
                    <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-400 px-2 py-1 rounded text-xs">
                      {language === 'ar' ? 'عملي' : 'Hands-on'}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </div>

            {/* Solar Energy Systems - COMING SOON */}
            <div 
              onClick={() => handleCourseClick("/solar-teaser")}
              className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-yellow-500 dark:hover:border-yellow-400 transition-all duration-300 cursor-pointer group hover:shadow-2xl hover:scale-105"
            >
              <div className="h-48 bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <div className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    {language === 'ar' ? 'قريباً' : 'COMING SOON'}
                  </div>
                </div>
                <div className="absolute top-4 right-4">
                  <div className="bg-yellow-300 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold">
                    {language === 'ar' ? '🔋 جديد' : '🔋 NEW'}
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="w-8 h-8 flex items-center justify-center">☀️</div>
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {language === 'ar' ? 'أنظمة الطاقة الشمسية' : 'Solar Energy Systems'}
                </h3>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded text-xs">
                      {language === 'ar' ? 'قريباً' : 'Coming Soon'}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-yellow-600 dark:group-hover:text-yellow-400 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </div>

            {/* Drone Building Course - COMING SOON */}
            <div 
              onClick={() => handleCourseClick("/drone-teaser")}
              className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-400 transition-all duration-300 cursor-pointer group hover:shadow-2xl hover:scale-105"
            >
              <div className="h-48 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <div className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                    {language === 'ar' ? 'قريباً' : 'COMING SOON'}
                  </div>
                </div>
                <div className="absolute top-4 right-4">
                  <div className="bg-green-300 text-green-900 px-3 py-1 rounded-full text-xs font-bold">
                    {language === 'ar' ? '🚁 متقدم' : '🚁 ADVANCED'}
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 text-white">
                  <div className="w-8 h-8 flex items-center justify-center">🛸</div>
                </div>
              </div>
              
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {language === 'ar' ? 'بناء طائرة بدون طيار مع PX4' : 'Build Your First Drone with PX4 Autopilot'}
                </h3>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400 px-2 py-1 rounded text-xs">
                      {language === 'ar' ? 'قريباً' : 'Coming Soon'}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/qubit-network-logo.svg" 
                  alt="Qubit Center" 
                  className="w-8 h-8"
                  style={{ filter: 'brightness(0) saturate(100%) invert(0.5)' }}
                />
                <span className="text-xl font-semibold text-gray-900 dark:text-white">Qubit Center</span>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                {language === 'ar' 
                  ? 'منصة تعليمية متطورة لتعلم التقنيات الحديثة في البطاريات والبرمجة.'
                  : 'Advanced learning platform for modern battery technology and programming education.'
                }
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'الدورات' : 'Courses'}
              </h3>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => handleCourseClick("/cpp-course")} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                    {language === 'ar' ? 'برمجة C++' : 'C++ Programming'}
                    <div className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded text-xs">
                      {language === 'ar' ? 'متاح' : 'OPEN'}
                    </div>
                  </button>
                </li>
                <li>
                  <button onClick={() => handleCourseClick("/lithium-teaser")} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                    {language === 'ar' ? 'بطاريات الليثيوم' : 'Lithium Batteries'}
                    <div className="bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-400 px-1.5 py-0.5 rounded text-xs">
                      {language === 'ar' ? 'قريباً' : 'SOON'}
                    </div>
                  </button>
                </li>
                <li>
                  <button onClick={() => handleCourseClick("/solar-teaser")} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-left">
                    {language === 'ar' ? 'الطاقة الشمسية' : 'Solar Energy'}
                  </button>
                </li>
                <li>
                  <button onClick={() => handleCourseClick("/drone-teaser")} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors text-left">
                    {language === 'ar' ? 'طائرات بدون طيار' : 'Drone Building'}
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'التواصل' : 'Contact'}
              </h3>
              <ul className="space-y-3">
                <li className="text-gray-600 dark:text-gray-400">
                  📧 info@qubitcenter.com
                </li>
                <li className="text-gray-600 dark:text-gray-400">
                  📱 +962 77 192 5032
                </li>
                <li className="text-gray-600 dark:text-gray-400">
                  📍 {language === 'ar' ? 'عمان، الأردن' : 'Amman, Jordan'}
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-200 dark:border-gray-700 mt-12 pt-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              © 2025 Qubit Center. {language === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
            </p>
          </div>
        </div>
      </footer>
      
      {/* Close dropdown when clicking outside */}
      {isCoursesDropdownOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setIsCoursesDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default MainHomepage;